/**
 * Generic MIDI send-cache - Rig State Milestone 2 foundation (2026-08-15,
 * PLAN.md QUEUED "Rig States" > "v1 build"). The CP-058 design's "dumb
 * send-cache": `Map<targetId, {value, raw, ts}>`, written at the point each
 * dispatch builder already has 0-127 normalized. v1 is 100% PWA-originated,
 * so this cache IS the ledger - no OPBridge output-state ledger, no rich
 * cross-transport type (that's deferred - see PLAN.md "Deliberately
 * DEFERRED out of v1").
 *
 * Distinct from `liveState/registry.ts` (Milestone 1, shipped): that wraps
 * the 4 named targets that already had their OWN reactive session signal
 * (transpose/capo/autotune key+scale). THIS cache is for everything else -
 * arbitrary MIDI CC / note targets with no dedicated signal of their own.
 * A Rig State bank's capture step will eventually need to read from
 * whichever of the two a given target actually has - that's Milestone 2's
 * capture-UI job, not this file's.
 *
 * Engine-driven + open-loop (unreadable) targets are deliberately never
 * written here - only what the PWA itself just sent. Per the locked
 * semantics: "Capture reads rendered output, from a per-target send-cache.
 * No hand-typed values."
 */

const TARGET_PREFIX_MIDI = 'midi';

export interface SendCacheEntry {
  /** Normalized 0..1. */
  value: number;
  /** Raw MIDI value (0-127), when the write came from a byte-space send. */
  raw?: number;
  /** `Date.now()` at write time. */
  ts: number;
}

const cache = new Map<string, SendCacheEntry>();

/** Write a raw 0-127 MIDI value into the cache, normalizing to 0..1. */
export function writeSendCacheRaw(targetId: string, raw: number): void {
  cache.set(targetId, { value: Math.max(0, Math.min(1, raw / 127)), raw, ts: Date.now() });
}

export function readSendCache(targetId: string): SendCacheEntry | undefined {
  return cache.get(targetId);
}

/** Every generic MIDI target sent this session, most-recent first - powers
 *  the Rig State authoring UI's "add a target" picker (Milestone 2's
 *  authoring-UI job, see file header). Session-ephemeral by construction:
 *  a target the operator hasn't sent since the page loaded can't appear
 *  here yet, same limitation the whole send-cache design already accepted. */
export function listSendCacheTargets(): Array<SendCacheEntry & { targetId: string }> {
  return [...cache.entries()]
    .map(([targetId, entry]) => ({ targetId, ...entry }))
    .sort((a, b) => b.ts - a.ts);
}

/** The port of a target sent on the bridge's DEFAULT output (S305,
 *  2026-10-06). A look now travels between PCs, and the default port's OS
 *  name differs between them (the Mac's `OPBridge-Ableton` is absent on the
 *  Windows daemon, DEC-108's correction), so a look captured on one PC would
 *  name a port the other does not have. Keyed by this token instead, recall
 *  resolves each bridge's own default. A step that named its own port (a
 *  Helix, DEC-149) keeps the name: that IS the destination, on any PC.
 *  Looks captured before this keep the name they were captured with. */
export const DEFAULT_PORT_TOKEN = '@default';

/** Stable, opaque, transport-agnostic target id - the CP-058 "one
 *  non-deferrable decision." MIDI-only for now (v1 scope); extensible to
 *  `osc:<addr>` / `dmx:<universe>:<ch>` / `obs:<source>:<field>` later
 *  without a re-keying migration, since every `TargetValueStep`/future Rig
 *  State bank member already stores this as an opaque string. */
export function midiCcTargetId(port: string, channel1to16: number, cc: number): string {
  return `${TARGET_PREFIX_MIDI}:${port}:cc:${channel1to16}:${cc}`;
}

export function midiNoteTargetId(port: string, channel1to16: number, note: number): string {
  return `${TARGET_PREFIX_MIDI}:${port}:note:${channel1to16}:${note}`;
}

/** Test-only escape hatch - smokes need a clean cache between runs within
 *  the same page (the cache is module-scoped, not per-timeline). */
export function _resetSendCacheForTest(): void {
  cache.clear();
}
